import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  DataTable,
  Button,
  ButtonGroup,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Checkbox,
  TextField,
  Badge
} from '@shopify/polaris';
import { reviewService } from '../../services/reviewService';
import { parseDurationToMinutes, getCommentText } from '../../utils/timeFormat';
import LoadingState from './LoadingState';

const periodLabel = (review) =>
  new Date(review.year, review.month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });

const statusTone = { pending: 'warning', approved: 'success', rejected: 'critical' };

// Shared by ReviewTimes.js (manager) and MonthlyReviews.js (admin) - the
// review/approve/reject UI is identical, only the listing source differs.
const ReviewDetail = ({ reviewId, onDecided }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reasons, setReasons] = useState({});
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await reviewService.getDetail(reviewId);
      setData(result);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load review');
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    load();
    setSelectedIds([]);
    setReasons({});
    setRejecting(false);
  }, [load]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleApprove = async () => {
    setSubmitting(true);
    setError('');
    try {
      await reviewService.approve(reviewId);
      await load();
      onDecided?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmReject = async () => {
    const rejectedEntries = selectedIds.map((id) => ({ timeEntryId: id, reason: (reasons[id] || '').trim() }));
    if (rejectedEntries.length === 0) {
      setError('Select at least one problem time entry');
      return;
    }
    if (rejectedEntries.some((e) => !e.reason)) {
      setError('Please provide a reason for each selected entry');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await reviewService.reject(reviewId, rejectedEntries);
      await load();
      onDecided?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState />;
  if (!data) return <Banner tone="critical">{error || 'Failed to load review'}</Banner>;

  const { review, timeEntries } = data;
  const isPending = review.status === 'pending';

  const rows = timeEntries.map((entry) => {
    const id = entry.id.toString();
    const workPackageTitle = entry._links?.workPackage?.title || 'N/A';
    const taskId = entry._links?.workPackage?.href?.split('/').pop() || 'N/A';
    const minutes = parseDurationToMinutes(entry.hours);
    const flaggedReason = review.rejectedEntries?.find((r) => r.timeEntryId === id)?.reason;

    return [
      isPending && rejecting ? (
        <Checkbox checked={selectedIds.includes(id)} onChange={() => toggleSelect(id)} label="Flag" labelHidden />
      ) : flaggedReason ? (
        <Badge tone="critical">Flagged</Badge>
      ) : (
        ''
      ),
      entry.spentOn || 'N/A',
      taskId,
      workPackageTitle,
      `${minutes} min`,
      getCommentText(entry.comment),
      flaggedReason || ''
    ];
  });

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <Text as="h2" variant="headingMd">
          {periodLabel(review)}
        </Text>
        <Badge tone={statusTone[review.status]}>{review.status}</Badge>
      </InlineStack>

      {error && <Banner tone="critical">{error}</Banner>}

      <Card>
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
          headings={[rejecting ? 'Flag' : review.status === 'rejected' ? 'Status' : '', 'Date', 'Task ID', 'Task', 'Duration', 'Comment', 'Reject reason']}
          rows={rows}
        />
      </Card>

      {isPending && !rejecting && (
        <ButtonGroup>
          <Button variant="primary" onClick={handleApprove} loading={submitting}>
            Approve
          </Button>
          <Button tone="critical" onClick={() => setRejecting(true)} disabled={submitting}>
            Reject
          </Button>
        </ButtonGroup>
      )}

      {isPending && rejecting && (
        <BlockStack gap="300">
          <Text as="p" tone="subdued">
            Select the problem entries above, then give a reason for each.
          </Text>
          {selectedIds.map((id) => {
            const entry = timeEntries.find((e) => e.id.toString() === id);
            return (
              <TextField
                key={id}
                label={`Reason - Task ${entry?._links?.workPackage?.href?.split('/').pop() || id} (${entry?.spentOn || ''})`}
                value={reasons[id] || ''}
                onChange={(value) => setReasons((prev) => ({ ...prev, [id]: value }))}
                multiline={2}
              />
            );
          })}
          <ButtonGroup>
            <Button tone="critical" onClick={handleConfirmReject} loading={submitting} disabled={selectedIds.length === 0}>
              Confirm Rejection
            </Button>
            <Button
              onClick={() => {
                setRejecting(false);
                setSelectedIds([]);
                setReasons({});
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </BlockStack>
      )}
    </BlockStack>
  );
};

export default ReviewDetail;
